
import axios from 'axios';
import { getToken } from '../hooks/useAuth';

/**
 * Configures an Axios instance with automatic retry logic for database "stale connection" errors.
 * This helps handle the "AttributeError: 'NoneType' object has no attribute 'read'" 
 * and "sqlalchemy.exc.InterfaceError" without requiring a page reload.
 */

const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Request interceptor for API calls
apiClient.interceptors.request.use(
    async (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for API calls
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const { config, response } = error;
        
        // If the error is a 500 Internal Server Error, it might be the stale connection issue
        // We can try to retry the request once. 
        // SQLAlchemy with pool_pre_ping=True will fix the connection on the second attempt.
        if (response && response.status === 500 && !config._retry) {
            console.warn('API Error (500) detected. Retrying once to handle potential stale connection...');
            config._retry = true;
            
            // Add a small delay before retrying
            await new Promise(resolve => setTimeout(resolve, 500));
            
            return apiClient(config);
        }

        // Handle 401 Unauthorized
        if (response && response.status === 401) {
            localStorage.removeItem('token');
            // Avoid infinite loops if we're already on login
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
        }

        return Promise.reject(error);
    }
);

export default apiClient;
